<!-- WHY: Unified response form with glassmorphism design and texture background -->
<div class="min-h-screen bg-gradient-to-br from-brand-briefcream via-brand-nude/20 to-brand-briefcream p-4"
  style="background-image: radial-gradient(circle at 1px 1px, rgba(214, 179, 112, 0.15) 1px, transparent 0); background-size: 20px 20px;">

  <!-- Main Form Container -->
  <div class="w-full max-w-4xl mx-auto">
    <!-- Glassmorphism Card -->
    <div
      class="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-white/40 overflow-hidden hover:shadow-3xl transition-all duration-300">

      <!-- Header Section -->
      <div class="bg-gradient-to-br from-brand-goldline/10 to-brand-nude/20 p-6 text-center">
        <div class="text-5xl text-brand-briefblue font-[Parisienne] leading-none mb-3">T & S</div>
        <div class="flex justify-center mb-3">
          <svg width="180" height="16" viewBox="0 0 180 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 8 Q30 0 50 8 T90 8 T130 8 T170 8" stroke="#D6B370" stroke-width="2" fill="none" />
            <circle cx="90" cy="8" r="4" fill="#F9F6F1" stroke="#D6B370" stroke-width="2" />
          </svg>
        </div>
        <h1 class="font-[Neuton] text-xl text-brand-inktint mb-1 font-semibold">
          {{ isWeddingForm() ? 'Zur Hochzeit von Tabea & Stephan' : 'Zum Polterabend von Tabea & Stephan' }}
        </h1>
        <p class="font-[Neuton] text-brand-inktint/70">
          Sagt uns Bescheid, ob ihr dabei seid
        </p>
      </div>

      <!-- Form -->
      <form [formGroup]="responseForm" (ngSubmit)="onSubmit()" class="p-6 space-y-6">


        <!-- Error Message -->
        @if (showError()) {
        <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div class="flex items-center">
            <span class="text-red-600 mr-3">❌</span>
            <p class="text-red-800 font-[Neuton]">{{ showError() }}</p>
          </div>
        </div>
        }

        <!-- Email Section (glassmorphism) -->
        <div
          class="bg-white/60 backdrop-blur-sm border border-white/40 rounded-lg p-4 shadow-lg flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <!-- Checkbox -->
          <label class="flex items-center cursor-pointer">
            <input type="checkbox" id="wants_updates" formControlName="wants_updates"
              class="w-4 h-4 text-brand-goldline border border-brand-goldline/40 rounded focus:ring-1 focus:ring-brand-goldline mr-2" />
            <span class="font-[Neuton] text-brand-inktint">E-Mail Updates erhalten</span>
          </label>

          <!-- Email input -->
          <div class="flex-1 justify-center">
            <label for="email" class="block font-[Neuton] text-brand-inktint mb-1 text-sm">E-Mail</label>
            <input type="email" id="email" formControlName="email" placeholder="eure-email@example.com"
              class="w-full p-2 bg-white/70 backdrop-blur-sm border rounded focus:border-brand-goldline focus:outline-none font-[Neuton] focus:bg-white/90 transition-all
            border-white/50
            @if (responseForm.get('email')?.invalid && responseForm.get('email')?.touched) border-red-400 @else border-white/50" />
            @if (responseForm.get('email')?.invalid && responseForm.get('email')?.touched) {
            <div class="text-xs text-red-600 mt-1">
              @if (responseForm.get('email')?.errors?.['required']) {
                E-Mail-Adresse ist erforderlich für Updates.
              } @else if (responseForm.get('email')?.errors?.['email']) {
                Bitte gib eine gültige E-Mail-Adresse ein.
              }
            </div>
            }
          </div>
        </div>


        <!-- Persons Section -->
        <div class="bg-white/60 backdrop-blur-sm border border-white/40 rounded-lg p-4 shadow-lg">
          <div class="flex items-center justify-between mb-4">
            @let persons = personCount();
            <h3 class="font-[Neuton] font-semibold text-brand-inktint">
              Wer kommt? ({{ persons }} {{ persons === 1 ? 'Person' : 'Personen' }})
            </h3>
            <button type="button" (click)="addPerson()"
              class="px-3 py-1 font-[Neuton] text-brand-inktint bg-brand-goldline/30 hover:bg-brand-goldline/50 backdrop-blur-sm rounded border border-brand-goldline/50 transition-all shadow-sm">
              + Person hinzufügen
            </button>
          </div>

          <div formArrayName="persons" class="space-y-4">
            @for (person of personsArray.controls; track $index) {
            <div [formGroupName]="$index"
              class="bg-white/40 backdrop-blur-sm border border-white/30 rounded-lg p-4 shadow-md">

              <!-- Name Row with Remove Button -->
              <div class="flex justify-between items-start mb-3">
                <div class="flex-1">
                  <label class="block font-[Neuton] text-brand-inktint mb-1 font-medium">Name</label>
                  <input type="text" formControlName="name"
                    class="w-full px-3 py-2 bg-white/70 backdrop-blur-sm border rounded focus:border-brand-goldline focus:outline-none font-[Neuton] focus:bg-white/90 transition-all
                           border-white/50
                           @if (person.get('name')?.invalid && person.get('name')?.touched) border-red-400 @else border-white/50"
                    placeholder="Vor- und Nachname">
                  @if (person.get('name')?.invalid && person.get('name')?.touched) {
                  <div class="text-xs text-red-600 mt-1">Bitte gib einen Namen an.</div>
                  }
                </div>
                @if (personCount() > 1) {
                <button type="button" (click)="removePerson($index)"
                  class="ml-4 text-red-600 hover:text-red-800 font-[Neuton] text-sm transition-colors">
                  Entfernen
                </button>
                }
              </div>

              <!-- Participation and Notes Row -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <!-- Event Participation -->
                <div>
                  <label class="block font-[Neuton] text-brand-inktint mb-1 font-medium">Teilnahme</label>
                  <div class="space-y-1">
                    @if (isWeddingForm()) {
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" formControlName="attendsWedding"
                        class="w-4 h-4 text-brand-goldline border border-brand-goldline/40 rounded focus:ring-1 focus:ring-brand-goldline" />
                      <span class="font-[Neuton] text-brand-inktint text-sm">Hochzeit</span>
                    </label>
                    }
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" formControlName="attendsPolterabend"
                        class="w-4 h-4 text-brand-goldline border border-brand-goldline/40 rounded focus:ring-1 focus:ring-brand-goldline" />
                      <span class="font-[Neuton] text-brand-inktint text-sm">Polterabend</span>
                    </label>
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" formControlName="attendsNothing"
                        class="w-4 h-4 text-brand-goldline border border-brand-goldline/40 rounded focus:ring-1 focus:ring-brand-goldline" />
                      <span class="font-[Neuton] text-brand-inktint text-sm">Kommt nicht</span>
                    </label>
                    @if (person.errors?.['noAttendanceSelected'] && person.touched) {
                    <div class="text-xs text-red-600 mt-1">Bitte wähle mindestens eine Option aus.</div>
                    }
                  </div>
                </div>

                <!-- Allergies/Comments -->
                <div>
                  <label class="block font-[Neuton] text-brand-inktint mb-1 font-medium">
                    Notizen
                  </label>
                  <textarea formControlName="allergies_comments"
                    class="w-full px-3 py-2 bg-white/70 backdrop-blur-sm border border-white/50 rounded focus:border-brand-goldline focus:outline-none font-[Neuton] resize-none text-sm focus:bg-white/90 transition-all"
                    rows="3" placeholder="Allergien, besondere Wünsche...">
                    </textarea>
                </div>
              </div>
            </div>
            }
          </div>
        </div>

        <!-- Polterabend-specific fields -->
        @if (hasPolterabendAttendees()) {
        <div class="bg-white/60 backdrop-blur-sm border border-white/40 rounded-lg p-4 shadow-lg">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <!-- Arrival Time -->
            <div>
              <label class="block font-[Neuton] text-brand-inktint mb-2 font-medium">Wann kommt ihr zum
                Polterabend?</label>
              <div class="space-y-2">
                @for (option of arrivalOptions(); track option.value) {
                <label class="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-white/50 transition-colors">
                  <input type="radio" [value]="option.value" formControlName="arrival_time"
                    class="w-4 h-4 text-brand-goldline border border-brand-goldline/60 focus:ring-2 focus:ring-brand-goldline/50 bg-white">
                  <span class="font-[Neuton] text-brand-inktint font-medium relative z-10" style="color: #2C3E50 !important;">{{ option.label }}</span>
                </label>
                }
              </div>
            </div>

            <!-- What to bring -->
            <div>
              <label class="block font-[Neuton] text-brand-inktint mb-2 font-medium">Wir wissen schon, dass wir etwas
                mitbringen!</label>
              <textarea formControlName="brings_items"
                class="w-full px-3 py-2 bg-white/90 backdrop-blur-sm border-2 border-brand-goldline/30 rounded-lg focus:border-brand-goldline focus:outline-none font-[Neuton] resize-none focus:bg-white transition-all shadow-sm"
                rows="3" placeholder="z.B. selbstgemachter Kartoffelsalat, Kuchen, Getränke, Grillgut...">
                </textarea>
              <div class="text-xs text-brand-inktint/60 mt-1 font-[Neuton]">Optional </div>
            </div>
          </div>
        </div>
        }


        <!-- Summary -->
        <div class="bg-brand-goldline/20 backdrop-blur-sm rounded-lg p-4 border border-brand-goldline/40 shadow-md">
          <h4 class="font-[Neuton] font-semibold text-brand-inktint mb-2">Zusammenfassung:</h4>
          <pre class="font-[Neuton] text-brand-inktint/90 text-sm whitespace-pre-wrap">{{ summaryText() }}</pre>
        </div>

        <!-- Additional Notes -->
        <div class="bg-white/60 backdrop-blur-sm border border-white/40 rounded-lg p-4 shadow-lg">
          <label for="additional_notes" class="block font-[Neuton] font-medium text-brand-inktint mb-2">
            Sonstige Notizen oder Wünsche
          </label>
          <textarea id="additional_notes" formControlName="additional_notes"
            class="w-full px-3 py-2 bg-white/70 backdrop-blur-sm border border-white/50 rounded focus:border-brand-goldline focus:outline-none font-[Neuton] resize-none focus:bg-white/90 transition-all"
            rows="3" placeholder="Habt ihr noch Fragen, Wünsche oder möchtet uns etwas mitteilen?">
          </textarea>
        </div>

        <!-- Submit Button -->
        <div class="flex flex-col sm:flex-row gap-4 justify-between items-center pt-4 border-t border-white/30">
          <a routerLink="{{ isWeddingForm() ? '/details' : '/polter' }}"
            class="font-[Neuton] text-brand-inktint/70 hover:text-brand-inktint transition-colors">
            ← Zurück zu den Infos
          </a>

          <button type="submit" [disabled]="isSubmitting() || !responseForm.valid"
            class="px-6 py-3 bg-gradient-to-r from-brand-inktint/90 to-brand-briefblue/90 hover:from-brand-inktint hover:to-brand-briefblue text-brand-briefcream font-[Neuton] font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-inktint/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl backdrop-blur-sm">
            @if (isSubmitting()) {
            <span>Wird gesendet...</span>
            } @else {
            <span>{{ isWeddingForm() ? 'Hochzeit-' : 'Polterabend-' }}Anmeldung absenden</span>
            }
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Success Overlay -->
  @if (showSuccess()) {
  <div class="fixed inset-0 bg-black/40 backdrop-blur-md z-50 flex items-center justify-center p-4">
    <div
      class="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/40 max-w-md w-full mx-auto overflow-hidden">
      <!-- Header Section -->
      <div class="bg-gradient-to-br from-brand-goldline/20 to-brand-nude/30 p-8 text-center">
        <div class="text-5xl text-brand-briefblue font-[Parisienne] leading-none mb-4">T & S</div>
        <div class="flex justify-center mb-4">
          <svg width="120" height="12" viewBox="0 0 120 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 6 Q20 0 34 6 T60 6 T86 6 T114 6" stroke="#D6B370" stroke-width="2" fill="none" />
            <circle cx="60" cy="6" r="3" fill="#F9F6F1" stroke="#D6B370" stroke-width="2" />
          </svg>
        </div>
        <h2 class="text-2xl font-[Neuton] font-semibold text-brand-inktint mb-2">
          Anmeldung eingegangen!
        </h2>
      </div>

      <!-- Content -->
      <div class="p-8 text-center">
        <p class="text-brand-inktint/90 font-[Neuton] text-lg mb-8 leading-relaxed">
          Perfekt! Eure Anmeldung ist angekommen. Wir freuen uns auf einen tollen {{ isWeddingForm() ? 'Tag' : 'Abend'
          }} mit euch!
        </p>

        <button (click)="closeSuccessOverlay()"
          class="px-8 py-3 bg-gradient-to-r from-brand-inktint/90 to-brand-briefblue/90 hover:from-brand-inktint hover:to-brand-briefblue text-brand-briefcream font-[Neuton] font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-inktint/50 transition-all duration-200 shadow-lg hover:shadow-xl backdrop-blur-sm">
          Schließen
        </button>
      </div>
    </div>
  </div>
  }
</div>